import { Configuration, StoresApi } from '@/api';
import { productListQueryKeys } from '@/api/queries/productListQueryKeys';
import { StoreFilters } from '@/api/StoresApi';
import AdditiveComboBox from '@/components/select/AdditiveComboBox';
import { AdditiveComboBoxProps } from '@/components/select/ComboBox';
import { Store, StoreAssociation } from '@/models/Store';
import { useAuth } from '@/modules/auth/contexts/AuthContext';

export interface StoreAdditiveComboBoxItem {
  id: string;
  name: string;
  store: Store;
}

interface StoreAdditiveComboBoxProps extends Omit<
  AdditiveComboBoxProps<StoreAdditiveComboBoxItem>,
  'queryFn' | 'queryKey'
> {
  supplierId?: string;
  productListId?: string;
}

const formatStoreName = (store: Store) => {
  const storeName = store.code ? `${store.name} (${store.code})` : store.name;
  const retailerName = store.retailer?.organization?.name ?? store.retailer?.name;

  return retailerName ? `${storeName} — ${retailerName}` : storeName;
};

const StoreAdditiveComboBox = ({
  supplierId,
  productListId,
  label = 'Stores:',
  placeholder = 'Search stores',
  selectedIds,
  ...props
}: StoreAdditiveComboBoxProps) => {
  const storesApi = new StoresApi(new Configuration(useAuth));

  const findAvailableStores = async (query: string = ''): Promise<StoreAdditiveComboBoxItem[]> => {
    const filters: Partial<StoreFilters> = {
      supplierId,
      supplierStoreStatus: 'ACTIVE',
      productListId,
      exclude: Boolean(productListId),
    };
    const response = await storesApi.findStores(query, filters, 0, 10, [
      StoreAssociation.RETAILER,
      StoreAssociation.ORGANIZATION,
    ]);

    return response.items.map((store) => ({
      id: store.id,
      name: formatStoreName(store),
      store,
    }));
  };

  return (
    <AdditiveComboBox<StoreAdditiveComboBoxItem>
      {...props}
      name="store"
      label={label}
      placeholder={placeholder}
      selectedIds={selectedIds}
      queryFn={findAvailableStores}
      queryKey={[
        ...productListQueryKeys.storeAssignmentOptions(productListId ?? ''),
        supplierId ?? '',
        'activeSupplierRelationship',
        ...(selectedIds ?? []).map(String),
      ]}
    />
  );
};

export default StoreAdditiveComboBox;
